import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ArrowRightLeft, BedDouble, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useBeds, useTransferBed } from '../../services/httpServices/queries';
import { SearchableSelect, type Option } from './searchable-select';

interface AdmissionTransferBedModalProps {
  open: boolean;
  admissionId: string;
  currentBedLabel: string;
  onClose: () => void;
}

const BED_TYPE_LABELS: Record<string, string> = {
  GENERAL: 'General',
  SEMI_PRIVATE: 'Semi Private',
  PRIVATE: 'Private',
  ICU: 'ICU',
  CCU: 'CCU',
  NICU: 'NICU',
  EMERGENCY: 'Emergency',
};

/**
 * Moves an admitted patient to another available bed. The backend also handles
 * the first bed assignment for an emergency admission that was registered
 * without one, so the same dialog covers both cases.
 */
export function AdmissionTransferBedModal({
  open,
  admissionId,
  currentBedLabel,
  onClose,
}: AdmissionTransferBedModalProps) {
  const { data, isLoading, error } = useBeds('AVAILABLE');
  const transferBed = useTransferBed();
  const [bedId, setBedId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setBedId('');
    setFormError(null);
  }, [open]);

  const beds = useMemo(
    () =>
      [...(data?.data ?? [])].sort((a, b) =>
        `${a.ward} ${a.bedNumber}`.localeCompare(`${b.ward} ${b.bedNumber}`),
      ),
    [data],
  );

  const bedOptions = useMemo<Option[]>(
    () =>
      beds.map((bed) => ({
        value: bed.id,
        label: `${bed.bedNumber} · ${bed.ward || 'Ward'}`,
        description: [
          BED_TYPE_LABELS[bed.type] || bed.type,
          bed.room ? `Room ${bed.room}` : '',
          Number(bed.price || 0) > 0
            ? `৳${Number(bed.price).toLocaleString()} / night`
            : 'Rate not set',
        ]
          .filter(Boolean)
          .join(' · '),
      })),
    [beds],
  );

  if (!open) return null;

  const handleSubmit = async () => {
    if (!bedId) {
      setFormError('Select a bed to transfer the patient to.');
      return;
    }
    setFormError(null);
    try {
      await transferBed.mutateAsync({ id: admissionId, bedId });
      const target = beds.find((bed) => bed.id === bedId);
      toast.success(
        target
          ? `Patient transferred to bed ${target.bedNumber} (${target.ward || 'ward'})`
          : 'Bed transferred',
      );
      onClose();
    } catch (err: any) {
      setFormError(
        err?.response?.data?.message ||
          err?.message ||
          'Could not transfer the patient to that bed.',
      );
    }
  };

  const rectError = error ? 'Could not load the available beds.' : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!transferBed.isPending) onClose();
      }}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-surface shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <ArrowRightLeft size={17} />
            </span>
            <div>
              <h2 className="text-sm font-semibold text-text">Transfer bed</h2>
              <p className="text-[0.6875rem] text-text-muted">
                Current bed: <span className="font-medium text-text">{currentBedLabel}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={transferBed.isPending}
            className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-background hover:text-text disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 p-5">
          {(rectError || formError) && (
            <div className="flex items-start gap-2 rounded-lg border border-danger/20 bg-danger/10 px-3 py-2 text-sm text-danger">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{formError || rectError}</span>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : beds.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-background/40 px-4 py-10 text-center">
              <BedDouble size={26} className="mb-2 text-text-muted/50" />
              <p className="text-sm font-medium text-text">No available beds</p>
              <p className="mt-1 text-xs text-text-muted">
                Free a bed or add a new one before transferring this patient.
              </p>
            </div>
          ) : (
            <div>
              <label className="mb-1.5 block text-xs font-medium text-text">
                Transfer to bed <span className="text-danger">*</span>
              </label>
              <SearchableSelect
                value={bedId}
                onChange={setBedId}
                options={bedOptions}
                placeholder="Search by bed number or ward…"
                disabled={transferBed.isPending}
              />
              <p className="mt-1.5 text-[0.6875rem] text-text-muted">
                Only beds that are currently available are listed. Bed charges
                continue from the new bed's nightly rate.
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-border px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={transferBed.isPending}
            className="px-4 py-2 text-sm text-text-muted transition-colors hover:text-text disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!bedId || transferBed.isPending || beds.length === 0}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {transferBed.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <ArrowRightLeft size={16} />
            )}
            Transfer bed
          </button>
        </div>
      </div>
    </div>
  );
}
